// One vocabulary for "how is this run doing", shared by the rail, cards,
// notifications and the run page header.
import type { RunRecord } from "@/api/types";

export type Tone = "working" | "attention" | "done" | "failed" | "idle";

export function runTone(r: Pick<RunRecord, "status">): Tone {
  if (r.status === "running") return "working";
  if (r.status === "success") return "done";
  if (r.status === "failure") return "failed";
  return "idle";
}

export const TONE_LABEL: Record<Tone, string> = {
  working: "Working",
  attention: "Needs attention",
  done: "Done",
  failed: "Failed",
  idle: "Idle",
};

export function runTitle(r: RunRecord): string {
  return r.jira_ticket || r.title || r.github_repo || r.job_id.slice(0, 8);
}

export function isToday(iso: string | undefined, now = new Date()): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
}

// Runs whose failure hasn't been looked at yet surface under "Needs attention".
// Acknowledgement is per-browser (localStorage); it's a to-do list, not an audit.
const ACK_KEY = "rushd.mc.acked";

function readAcked(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(ACK_KEY) ?? "[]") as string[]);
  } catch {
    return new Set();
  }
}

export function isAcked(jobId: string): boolean {
  return readAcked().has(jobId);
}

export function ackRun(jobId: string): void {
  try {
    const s = readAcked();
    s.add(jobId);
    localStorage.setItem(ACK_KEY, JSON.stringify([...s].slice(-500)));
  } catch {
    // storage blocked (private mode) — acknowledgement just won't persist
  }
}

export function needsAttention(r: RunRecord): boolean {
  return r.status === "failure" && !isAcked(r.job_id);
}

/** A run's state as one badge: a tone for colour and the words that carry it. */
export function runStatus(r: Pick<RunRecord, "status" | "pr_url" | "awaiting_approval_at" | "approved_by" | "rejected_by"
  | "plan_outcome" | "deduped" | "canceled_by" | "fix_applied" | "flow">): { tone: Tone; label: string } {
  if (r.status === "running") {
    return r.awaiting_approval_at && !r.approved_by && !r.rejected_by ? { tone: "attention", label: "Plan to review" } : { tone: "working", label: "Running" };
  }
  if (r.status === "failure") return r.canceled_by ? { tone: "idle", label: "Stopped" } : { tone: "failed", label: "Failed" };
  if (r.plan_outcome === "rejected") return { tone: "idle", label: "Plan rejected" };
  if (r.plan_outcome === "expired") return { tone: "idle", label: "Plan expired" };
  if (r.deduped) return { tone: "idle", label: "Duplicate" };
  if (r.pr_url) return { tone: "done", label: "PR opened" };
  if (r.flow === "f/jira_bug_analysis_deep/main") return { tone: "done", label: "Analysed" };
  if (r.fix_applied === false) return { tone: "done", label: "No change needed" };
  return { tone: "done", label: "Finished" };
}
